import { getCookie } from '@frontend/common';
import { FunctionalityClient } from '@frontend/functionality/api';
import { FeatureTarget, Functionality, FunctionalityType } from '@frontend/functionality/types';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { LoadingScreen } from '../loading-screen/loading-screen';

const LicenseStoreContext = createContext<{
    isLoading: boolean;
    functionalities: Functionality[];
} | null>(null);

const ID = 'license-store-provider';
const TELLOPORT_SELECTED_ACCOUNT_COOKIE = 'tp_prev_sel_acc';
export const LicenseStoreProvider = (props: { children: React.ReactNode; targets?: FeatureTarget[]; disabled?: boolean }) => {
    // Mixed-model decision: action/feature gating uses the *selected* (A) account's licenses,
    // not the foreign entity's (B) account. Users act with A's functionalities while viewing shared B data.
    const account_id = getCookie(TELLOPORT_SELECTED_ACCOUNT_COOKIE) ?? null;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [functionalities, setFunctionalities] = useState<Functionality[] | null>(null);
    const corporateFunctionalities = functionalities ? functionalities.filter((f) => f.type === FunctionalityType.CORPORATE) : [];
    const personalFunctionalities = functionalities ? functionalities.filter((f) => f.type === FunctionalityType.PERSONAL) : [];
    const [defaultFunctionality, setDefaultFunctionality] = useState<Functionality | null>(null);
    const [isDefaultFunctionalityLoading, setIsDefaultFunctionalityLoading] = useState<boolean>(true);
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const redirectToMissingLicense = () => {
        if (!window.location.pathname.includes('/standalone')) {
            navigate('/missing-license');
        }
    };
    const redirectToFeature = (functionalities: Functionality[]) => {
        if (!window.location.pathname.includes('/standalone')) {
            const defaultFunc = functionalities.find((f) => f.default) ?? functionalities[0];
            if (defaultFunc) {
                navigate(`/${defaultFunc.application}/${defaultFunc.feature}`);
            } else {
                redirectToMissingLicense();
            }
        }
    };

    async function fetchFunctionalities(accountId: string) {
        const fetchedFunctionalities = await FunctionalityClient.fetchFunctionalities({
            account_id: accountId,
            sort_direction: 'asc',
            sort_field: 'order',
            target: props.targets,
            size: '100'
        })
            .then((res) => res.results)
            .catch(() => []);
        setFunctionalities(fetchedFunctionalities);
    }

    useEffect(() => {
        if (props.disabled) {
            setFunctionalities([]);
            setIsLoading(false);
            setIsDefaultFunctionalityLoading(false);
            return;
        }
        if (pathname.includes('/standalone')) {
            setFunctionalities([]);
            setIsLoading(false);
            setIsDefaultFunctionalityLoading(false);
            return;
        }
        if (!account_id) {
            return;
        }
        fetchFunctionalities(account_id).finally(() => setIsLoading(false));
    }, [account_id, pathname, props.disabled]);

    useEffect(() => {
        if (!functionalities) return;
        if (functionalities.length === 0) {
            setDefaultFunctionality(null);
        } else {
            const defaultFunctionalities = functionalities.filter((func) => func.default === true);
            if (defaultFunctionalities.length > 0) {
                setDefaultFunctionality(defaultFunctionalities[0]);
            } else {
                setDefaultFunctionality(functionalities[0]);
            }
        }

        setIsDefaultFunctionalityLoading(false);
    }, [functionalities]);

    useEffect(() => {
        if (props.disabled) return;
        if (pathname !== '/' && pathname !== '/login') return;
        if (isLoading || isDefaultFunctionalityLoading || !functionalities) return;

        if (functionalities.length === 0) {
            redirectToMissingLicense();
            return;
        }

        const functionality = defaultFunctionality ?? functionalities[0];

        if (!functionality) {
            redirectToMissingLicense();
            return;
        }

        redirectToFeature(functionalities);
    }, [pathname, defaultFunctionality, functionalities, isLoading, isDefaultFunctionalityLoading, navigate, props.disabled]);

    if (functionalities === null) {
        return (
            <div className='flex min-h-screen min-w-screen items-center justify-center bg-white'>
                <div className='flex flex-col items-center gap-8'>
                    <LoadingScreen />
                </div>
            </div>
        );
    }
    return (
        <LicenseStoreContext.Provider
            value={{
                isLoading,
                functionalities
            }}>
            {props.children}
        </LicenseStoreContext.Provider>
    );
};

export const useLicenseRepository = () => {
    const context = useContext(LicenseStoreContext);
    if (!context) {
        throw new Error('useLicenseRepository must be used within a LicenseStoreProvider');
    }
    return context;
};
